import { useEffect, useState } from "react"

import type { ApiResult } from "@shared/types"

export type Nav = (url: string) => void

export function useNav(): Nav {
  const [, force] = useState(0)
  useEffect(() => {
    const on = () => force((n) => n + 1)
    addEventListener("popstate", on)
    return () => removeEventListener("popstate", on)
  }, [])
  return (url: string) => {
    history.pushState({ inApp: true }, "", url)
    force((n) => n + 1)
  }
}

// Anything whose .json() resolves to the API envelope — both plain fetch
// Responses and hono/client responses qualify, so T infers from hc calls.
export type ApiResponse<T> = { json(): Promise<ApiResult<T>> }

export function useApi<T>(call: (() => Promise<ApiResponse<T>>) | null, deps: unknown[]) {
  const [data, setData] = useState<T>()
  const [err, setErr] = useState<string>()
  useEffect(() => {
    let live = true
    setData(undefined)
    setErr(undefined)
    if (!call) return
    call()
      .then((r) => r.json())
      .then((b) => {
        if (!live) return
        if (b.status === "ok") setData(b.data)
        else setErr(String(b.data))
      })
      .catch((e: unknown) => live && setErr(String(e)))
    return () => {
      live = false
    }
  }, deps) // `call` is a fresh closure every render, so callers pass explicit keys
  return { data, err }
}
